import { apiFetch } from '@/lib/api/client';

/**
 * 카카오맵 SDK 로더.
 *
 * ## 키는 번들이 아니라 서버에서 받는다
 * `VITE_` 환경변수로 넣으면 **빌드 시점에 박혀** 키를 갈아 끼울 때마다 다시 빌드해야 한다. 이 앱의
 * 다른 키들은 전부 「환경설정 › 외부 API 설정」 화면에서 넣고 즉시 반영되는데, 지도만 예외가 되면
 * 담당자는 두 곳을 기억해야 한다. 그래서 서버가 내려주는 값을 쓴다(`/settings/external-api/map-key`).
 *
 * ## 이 키는 드러나도 되는 값이다
 * 지도 SDK 는 브라우저가 직접 카카오를 부르므로 키가 화면 소스에 보이는 것이 전제다. 방어는 카카오
 * 콘솔의 **사이트 도메인 등록**이고, 등록되지 않은 주소에서는 남이 키를 알아도 지도가 뜨지 않는다.
 * REST 키(도메인 제한 없음)와는 <b>다른 값</b>이며 섞으면 안 된다.
 *
 * ## 한 번만 받는다
 * 스크립트도 키도 처음 지도를 여는 순간 한 번 가져오고, 그 뒤로는 같은 약속을 돌려준다.
 * `autoload=false` 로 받아 `kakao.maps.load` 가 끝난 뒤에야 준비됐다고 본다 — 그러지 않으면
 * 스크립트는 왔는데 `kakao.maps.Map` 이 아직 없는 순간에 지도를 그리려 든다.
 */

type KakaoLatLng = object;

type KakaoOverlay = { setMap: (map: KakaoMap | null) => void };

export type KakaoMap = object;

export type KakaoNamespace = {
  maps: {
    LatLng: new (lat: number, lng: number) => KakaoLatLng;
    LatLngBounds: new () => { extend: (latlng: KakaoLatLng) => void };
    Map: new (container: HTMLElement, options: { center: KakaoLatLng; level?: number; scrollwheel?: boolean }) => KakaoMap & {
      setBounds: (bounds: object, paddingTop?: number, paddingRight?: number, paddingBottom?: number, paddingLeft?: number) => void;
      relayout: () => void;
    };
    CustomOverlay: new (options: {
      position: KakaoLatLng;
      content: string;
      yAnchor?: number;
      xAnchor?: number;
      zIndex?: number;
    }) => KakaoOverlay;
    Polyline: new (options: {
      path: KakaoLatLng[];
      strokeWeight?: number;
      strokeColor?: string;
      strokeOpacity?: number;
      strokeStyle?: string;
    }) => KakaoOverlay;
    Circle: new (options: {
      center: KakaoLatLng;
      radius: number;
      strokeWeight?: number;
      strokeColor?: string;
      strokeOpacity?: number;
      strokeStyle?: string;
      fillColor?: string;
      fillOpacity?: number;
    }) => KakaoOverlay;
    load: (callback: () => void) => void;
  };
};

declare global {
  interface Window {
    kakao?: KakaoNamespace;
  }
}

let pending: Promise<KakaoNamespace | null> | null = null;

/**
 * SDK 를 준비해 돌려준다. **키가 없으면 `null`** 이다 — 오류가 아니라 「아직 설정 안 됨」 이고,
 * 화면은 그때 지도 자리에 그 사실을 적는다.
 */
export function loadKakaoMaps(): Promise<KakaoNamespace | null> {
  if (window.kakao?.maps?.Map) return Promise.resolve(window.kakao);
  if (pending) return pending;

  pending = (async () => {
    const { key } = await apiFetch<{ key: string }>('/v1/settings/external-api/map-key');
    if (!key) return null;

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      // autoload=false 로 받아야 아래 load() 로 준비 시점을 잡을 수 있다.
      script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${encodeURIComponent(key)}&autoload=false`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('카카오맵을 불러오지 못했습니다.'));
      document.head.appendChild(script);
    });

    await new Promise<void>((resolve) => window.kakao!.maps.load(resolve));
    return window.kakao ?? null;
  })().catch((ex) => {
    // 실패한 약속을 남기면 다음 시도도 영영 실패한다.
    pending = null;
    throw ex;
  });

  return pending;
}
